import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { updatePatient } from "../../../../api/services/api";
import { toast } from "react-toastify";
import { Gender } from "../../../../api/types/api.types";

interface EditPatientDialogProps {
  open: boolean;
  onClose: () => void;
  patientData: {
    id: number;
    users?: {
      fullName?: string;
      email?: string;
      mobileNumber?: string;
    };
    gender?: Gender;
    age?: number;
    dob?: string;
  };
}

function EditPatientDialog({
  open,
  onClose,
  patientData,
}: EditPatientDialogProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    fullName: patientData.users?.fullName || "",
    email: patientData.users?.email || "",
    mobileNumber: patientData.users?.mobileNumber || "",
    gender: patientData.gender || "",
    age: patientData.age || "",
    dob: patientData.dob || "",
  });

  useEffect(() => {
    setFormData({
      fullName: patientData.users?.fullName || "",
      email: patientData.users?.email || "",
      mobileNumber: patientData.users?.mobileNumber || "",
      gender: patientData.gender || "",
      age: patientData.age || "",
      dob: patientData.dob || "",
    });
  }, [patientData]);

  const updateMutation = useMutation(
    (data: any) => updatePatient({ id: patientData.id, body: data }),
    {
      onSuccess: () => {
        toast.success("Patient details updated successfully!");
        queryClient.invalidateQueries(["patient", patientData.id]);
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to update patient details. Please try again."
        );
      },
    }
  );

  const handleChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = () => {
    const updateData: any = {};

    // Only include changed fields
    if (formData.fullName !== (patientData.users?.fullName || "")) {
      updateData.fullName = formData.fullName;
    }
    if (formData.email !== (patientData.users?.email || "")) {
      updateData.email = formData.email;
    }
    if (formData.mobileNumber !== (patientData.users?.mobileNumber || "")) {
      updateData.mobileNumber = formData.mobileNumber;
    }
    if (formData.gender !== (patientData.gender || "")) {
      updateData.gender = formData.gender;
    }
    if (formData.age !== (patientData.age || "")) {
      updateData.age = Number(formData.age);
    }
    if (formData.dob !== (patientData.dob || "")) {
      updateData.dob = formData.dob;
    }

    if (Object.keys(updateData).length === 0) {
      toast.info("No changes to save");
      return;
    }

    updateMutation.mutate(updateData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Patient Details</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.fullName}
              onChange={handleChange("fullName")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Mobile Number"
              value={formData.mobileNumber}
              onChange={handleChange("mobileNumber")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                label="Gender"
                onChange={handleChange("gender")}
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHERS">Others</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Age"
              type="number"
              value={formData.age}
              onChange={handleChange("age")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.dob}
              onChange={handleChange("dob")}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isLoading}
        >
          {updateMutation.isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditPatientDialog;
